import { useEffect, useState } from 'react';
import {
  FiX, FiEdit2, FiAlertCircle, FiCheckCircle,
  FiInfo, FiChevronUp, FiChevronDown, FiPlus, FiTag,
  FiTrash2, FiBell,
} from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import ToggleSwitch from '../settings/ToggleSwitch';
import ButtonLoading from '../ButtonLoading';

const inputClass = "w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white placeholder-gray-400 transition-all";
const selectClass = "w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white transition-all";

const EditInvoiceModal = ({ closeModal, payment, schoolId, schoolCode, onSuccess }) => {
  const classId = payment?.class?._id;
  const paymentId = payment?._id;
  const childId = payment?.child?._id;

  const [availableAddons, setAvailableAddons] = useState([]);
  const [transportAddons, setTransportAddons] = useState([]);

  // ordered list of selected addon ids — position = priority
  const [selectedAddons, setSelectedAddons] = useState(
    (payment?.add_ons || []).filter((a) => a?.addon_id || a?._id).map((a) => a.addon_id || a._id)
  );
  const [transportAddOnId, setTransportAddOnId] = useState(
    payment?.transport_add_on?.addon_id || ''
  );
  const [session, setSession] = useState(payment?.session || '');
  const [term, setTerm] = useState(payment?.term ? String(payment.term) : '');
  const [discountAmount, setDiscountAmount] = useState(
    payment?.discount?.amount ? String(payment.discount.amount) : ''
  );
  const [arrears, setArrears] = useState(
    (payment?.arrears || []).map((a) => ({ name: a.name || '', description: a.description || '', amount: String(a.amount ?? '') }))
  );
  const [arrearDraft, setArrearDraft] = useState({ name: '', description: '', amount: '' });
  const [showArrearForm, setShowArrearForm] = useState(false);
  const [notifyParents, setNotifyParents] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });

  useEffect(() => {
    const fetchAddons = async () => {
      if (!schoolId || !classId || !schoolCode) return;
      try {
        setLoading(true);
        const [addonsRes, transportRes] = await Promise.all([
          userRequest.get(`class-addon/${schoolId}/${classId}`),
          userRequest.get(`transport-addon?code=${schoolCode}`),
        ]);
        setAvailableAddons((addonsRes.data.add_on || []).filter((a) => a?._id));
        setTransportAddons(transportRes.data.transport_add_on || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddons();
  }, [schoolId, classId, schoolCode]);

  const addAddon = (id) => {
    if (!selectedAddons.includes(id)) setSelectedAddons((prev) => [...prev, id]);
  };

  const removeAddon = (id) => setSelectedAddons((prev) => prev.filter((a) => a !== id));

  const moveUp = (index) => {
    if (index === 0) return;
    setSelectedAddons((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setSelectedAddons((prev) => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const buildAddOnsPayload = () =>
    selectedAddons.map((id, i) => ({ addon_id: id, priority: i + 1 }));

  const addArrear = () => {
    if (!arrearDraft.name.trim() || !arrearDraft.amount) return;
    setArrears((prev) => [...prev, { ...arrearDraft, name: arrearDraft.name.trim() }]);
    setArrearDraft({ name: '', description: '', amount: '' });
    setShowArrearForm(false);
  };

  const removeArrear = (index) => {
    setArrears((prev) => prev.filter((_, i) => i !== index));
  };

  const arrearsTotal = arrears.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);

  const handleSave = async () => {
    if (!paymentId || !childId) return;
    setSaving(true);
    setAlert({ type: '', message: '' });
    try {
      const body = {
        add_ons: buildAddOnsPayload(),
        transport_add_on: transportAddOnId || '',
        ...(session && { session }),
        ...(term && { term: Number(term) }),
        ...(discountAmount && { discount_amount: Number(discountAmount) }),
        ...(arrears.length > 0 && { arrears }),
        notify_parents: notifyParents,
      };
      await userRequest.patch(`/payments/${paymentId}/${childId}`, body);
      setAlert({ type: 'success', message: 'Invoice updated successfully.' });
      setTimeout(() => { onSuccess?.(); closeModal(); }, 1500);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to update invoice. Please try again.';
      setAlert({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const studentName = `${payment?.child?.firstname || ''} ${payment?.child?.lastname || ''}`.trim();
  const addonById = Object.fromEntries(availableAddons.map((a) => [a._id, a]));
  const unselectedAddons = availableAddons.filter((a) => !selectedAddons.includes(a._id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-xl">
              <FiEdit2 className="text-indigo-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Edit Invoice</h2>
              <p className="text-xs text-gray-400 capitalize">{studentName}</p>
            </div>
          </div>
          <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-5 flex-1">

          {/* Alert */}
          {alert.message && (
            <div className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm border ${alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {alert.type === 'success' ? <FiCheckCircle className="mt-0.5 shrink-0" /> : <FiAlertCircle className="mt-0.5 shrink-0" />}
              <span>{alert.message}</span>
            </div>
          )}

          {/* Current fee summary */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Current Invoice</p>
            <div className="flex justify-between">
              <span className="text-gray-600">School Fees</span>
              <span className="font-semibold text-gray-800">{formatCurrency(payment?.class_price)}</span>
            </div>
            {(payment?.add_ons || []).map((a) => (
              <div key={a._id} className="flex justify-between">
                <span className="text-gray-500 text-xs">{a.add_on_name}</span>
                <span className="text-xs text-gray-600">{formatCurrency(a.add_on_price)}</span>
              </div>
            ))}
            {payment?.transport_add_on?.add_on_name && (
              <div className="flex justify-between">
                <span className="text-gray-500 text-xs">Transport — {payment.transport_add_on.add_on_name}</span>
                <span className="text-xs text-gray-600">{formatCurrency(payment.transport_add_on.add_on_price)}</span>
              </div>
            )}
            {(payment?.arrears || []).map((a) => (
              <div key={a._id || a.name} className="flex justify-between">
                <span className="text-red-500 text-xs">{a.name}</span>
                <span className="text-xs text-red-600">{formatCurrency(a.amount)}</span>
              </div>
            ))}
            {payment?.discount?.amount > 0 && (
              <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
                <span className="text-indigo-600 text-xs font-medium">Discount applied</span>
                <span className="text-xs text-indigo-700 font-semibold">−{formatCurrency(payment.discount.amount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
              <span className="text-gray-700 text-xs font-semibold">Total</span>
              <span className="text-xs font-bold text-gray-900">{formatCurrency(payment?.total_amount)}</span>
            </div>
          </div>

          {/* Session & Term */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Session</label>
              <input type="text" value={session} onChange={(e) => setSession(e.target.value)} placeholder="e.g. 2024/2025" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Term</label>
              <select value={term} onChange={(e) => setTerm(e.target.value)} className={selectClass}>
                <option value="">Select term</option>
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
                <option value="3">Term 3</option>
              </select>
            </div>
          </div>

          {/* Discount — prominent */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <FiTag size={14} className="text-amber-600" />
              <p className="text-sm font-semibold text-amber-800">Apply Discount</p>
            </div>
            <p className="text-xs text-amber-700 mb-3">Enter a discount amount to reduce the total invoice. Leave blank to keep the current discount.</p>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">₦</span>
              <input
                type="number"
                min={0}
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="0"
                className="w-full pl-8 pr-4 py-2.5 text-sm bg-white border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Extra fees */}
          {!loading && availableAddons.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Extra Fees</p>

              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800">
                <FiInfo size={13} className="mt-0.5 shrink-0 text-blue-400" />
                <p>Tap an add-on to include it. Use the arrows to set the order — the first item has the highest priority.</p>
              </div>

              {/* Unselected addons */}
              {unselectedAddons.length > 0 && (
                <div className="space-y-1.5">
                  {unselectedAddons.map((addon) => (
                    <button
                      key={addon._id}
                      type="button"
                      onClick={() => addAddon(addon._id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-colors group"
                    >
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-700 group-hover:text-indigo-800">{addon.add_on_name}</p>
                        <p className="text-xs text-gray-400">{formatCurrency(addon.add_on_price)}</p>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-gray-200 group-hover:bg-indigo-500 flex items-center justify-center transition-colors">
                        <FiPlus size={13} className="text-gray-500 group-hover:text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected addons (ordered) */}
              {selectedAddons.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 font-medium">Added — reorder to set priority</p>
                  {selectedAddons.map((id, index) => {
                    const addon = addonById[id];
                    if (!addon) return null;
                    return (
                      <div key={id} className="flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                        <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-indigo-900">{addon.add_on_name}</p>
                          <p className="text-xs text-indigo-500">{formatCurrency(addon.add_on_price)}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button type="button" onClick={() => moveUp(index)} disabled={index === 0}
                            className="w-7 h-7 rounded-full bg-white border border-indigo-200 flex items-center justify-center text-indigo-500 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all">
                            <FiChevronUp size={14} />
                          </button>
                          <button type="button" onClick={() => moveDown(index)} disabled={index === selectedAddons.length - 1}
                            className="w-7 h-7 rounded-full bg-white border border-indigo-200 flex items-center justify-center text-indigo-500 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all">
                            <FiChevronDown size={14} />
                          </button>
                        </div>
                        <button type="button" onClick={() => removeAddon(id)}
                          className="p-1 text-indigo-300 hover:text-red-500 transition-colors">
                          <FiX size={15} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!loading && availableAddons.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-1">No extra fees available for this class.</p>
          )}

          {/* Transport */}
          {!loading && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Transport Fee</label>
              <select value={transportAddOnId} onChange={(e) => setTransportAddOnId(e.target.value)} className={selectClass}>
                <option value="">None</option>
                {transportAddons.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.add_on_name} — {formatCurrency(t.add_on_price)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Arrears */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Arrears</p>
              {!showArrearForm && (
                <button
                  type="button"
                  onClick={() => setShowArrearForm(true)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  <FiPlus size={13} /> Add Line Item
                </button>
              )}
            </div>

            {arrears.length === 0 && !showArrearForm && (
              <p className="text-xs text-gray-400">No outstanding balances added to this invoice.</p>
            )}

            {arrears.length > 0 && (
              <div className="space-y-1.5">
                {arrears.map((a, index) => (
                  <div key={index} className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-red-900">{a.name}</p>
                      {a.description && <p className="text-xs text-red-500">{a.description}</p>}
                    </div>
                    <p className="text-sm font-semibold text-red-700 shrink-0">{formatCurrency(a.amount)}</p>
                    <button
                      type="button"
                      onClick={() => removeArrear(index)}
                      className="p-1 text-red-300 hover:text-red-600 transition-colors shrink-0"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between px-1 pt-1">
                  <p className="text-xs text-gray-500">Arrears total</p>
                  <p className="text-xs font-semibold text-gray-700">{formatCurrency(arrearsTotal)}</p>
                </div>
              </div>
            )}

            {showArrearForm && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2.5">
                <input
                  type="text"
                  placeholder="Name (e.g. Previous term fees)"
                  value={arrearDraft.name}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, name: e.target.value }))}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Description (optional, e.g. 2025/2026 Term 1)"
                  value={arrearDraft.description}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, description: e.target.value }))}
                  className={inputClass}
                />
                <input
                  type="number"
                  placeholder="Amount (₦)"
                  value={arrearDraft.amount}
                  onChange={(e) => setArrearDraft((d) => ({ ...d, amount: e.target.value }))}
                  className={inputClass}
                />
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setShowArrearForm(false); setArrearDraft({ name: '', description: '', amount: '' }); }}
                    className="flex-1 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addArrear}
                    disabled={!arrearDraft.name.trim() || !arrearDraft.amount}
                    className="flex-1 px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notify parents */}
          <div className="pt-1 border-t border-gray-100">
            <div className="flex items-center gap-2 mt-3 mb-1">
              <FiBell className="text-gray-400" size={13} />
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Notifications</p>
            </div>
            <ToggleSwitch
              label="Notify parents by email & push notification"
              initialState={notifyParents}
              onChange={setNotifyParents}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <p className="text-xs text-gray-400">
            {discountAmount ? `Discount: −${formatCurrency(discountAmount)}` : selectedAddons.length > 0 ? `${selectedAddons.length} add-on${selectedAddons.length > 1 ? 's' : ''}` : 'School fees only'}
            {arrears.length > 0 && ` · ${arrears.length} arrear${arrears.length > 1 ? 's' : ''}`}
          </p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={closeModal} disabled={saving}
              className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            {saving ? (
              <ButtonLoading styles="px-5 py-2 text-sm font-medium text-white bg-indigo-500 rounded-full" />
            ) : (
              <button type="button" onClick={handleSave} disabled={loading}
                className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors disabled:bg-indigo-300">
                Save Changes
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
